import {
  Alert,
  Badge,
  Button,
  ChangeSummary,
  DiffList,
  DiffRow,
  Drawer,
  Dropzone,
  FileList,
  Inline,
  List,
  SkeletonText,
  Stack,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  useToast,
  VisuallyHidden,
} from '@hkdevs/design-system';
import { useState, type ReactNode } from 'react';
import { ApiError } from '../../api/client';
import {
  errorReportOf,
  useApplyOrgImport,
  usePreviewOrgImport,
  type ErrorReport,
  type OrgImportPreview,
} from '../../api/queries/orgStructure';
import type { Role } from '../../api/queries/users';
import { withReference } from '../../api/withReference';
import { ROLE_LABELS } from './UserModal';
import { ROLE_TONES } from './UsersView';

/**
 * Import org structure (pc-dbb.8): a right drawer in two steps. Step 1 takes a CSV or
 * Excel file and explains how it's read (ADR 0015's columns, and ENABLED and ROLES from
 * ADR 0029), with the current structure to download as the template. Step 2 shows what
 * applying it changes, or the file's problems, row by row, like an upload's error report.
 */

type AffectedUser = OrgImportPreview['affected'][number];

export const IMPORT_STEPS = ['Select file', 'Review changes'] as const;

const ACCEPT = '.csv,.xlsx,.xlsm';

const names = (list: readonly string[]) => (list.length ? list.join(', ') : '—');

/** A column name. */
const Col = ({ children }: { children: ReactNode }) => <Text as="code">{children}</Text>;

/** Role badges, as the Users list shows them. */
const RoleBadges = ({ roles }: { roles: readonly Role[] }) => (
  <Inline as="span" gap={1} align="stretch">
    {roles.map((role) => (
      <Badge key={role} tone={ROLE_TONES[role]} size="sm">
        {ROLE_LABELS[role]}
      </Badge>
    ))}
  </Inline>
);

const disabledBadge = (
  <Badge tone="danger" size="sm">
    Disabled
  </Badge>
);

const sameNames = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((name) => b.includes(name));

/** A user's name, with lines under it (what changes, their email change). */
const Who = ({ name, lines }: { name: string; lines: ReactNode[] }) =>
  lines.length === 0 ? (
    name
  ) : (
    <Stack gap={1} as="span">
      <span>{name}</span>
      {lines.map((line, index) => (
        <Text key={index} tone="muted" size="meta">
          {line}
        </Text>
      ))}
    </Stack>
  );

/**
 * The diff rows for one affected user: their supervisors (as before ADR 0029), then
 * their status and their roles when the import changes them. A new user is one row, with
 * the roles they get (and Disabled) under their name.
 */
function AffectedRows({ change }: { change: AffectedUser }) {
  const key = change.userId ?? change.email;
  if (change.created) {
    const badges =
      change.rolesTo || change.enabled === false ? (
        <Inline as="span" gap={1} align="stretch">
          {change.rolesTo && <RoleBadges roles={change.rolesTo} />}
          {change.enabled === false && disabledBadge}
        </Inline>
      ) : null;
    return (
      <DiffRow
        name={<Who name={change.name} lines={badges ? [badges] : []} />}
        to={names(change.to)}
      />
    );
  }
  const rows: { kind: string; label?: string; from: ReactNode; to: ReactNode }[] = [];
  const others = change.enabled !== null || change.rolesTo !== null;
  if (!sameNames(change.from, change.to) || (change.previousEmail && !others)) {
    rows.push({ kind: 'supervisors', from: names(change.from), to: names(change.to) });
  }
  if (change.enabled !== null) {
    rows.push({
      kind: 'status',
      label: 'Status',
      from: change.enabled ? 'Disabled' : 'Enabled',
      to: change.enabled ? 'Enabled' : disabledBadge,
    });
  }
  if (change.rolesTo) {
    rows.push({
      kind: 'roles',
      label: 'Roles',
      from: change.rolesFrom?.length ? <RoleBadges roles={change.rolesFrom} /> : undefined,
      to: <RoleBadges roles={change.rolesTo} />,
    });
  }
  return (
    <>
      {rows.map((row, index) => {
        const lines: ReactNode[] = [];
        if (row.label) lines.push(row.label);
        if (index === 0 && change.previousEmail) {
          lines.push(`Email: ${change.previousEmail} → ${change.email}`);
        }
        return (
          <DiffRow
            key={`${key}-${row.kind}`}
            name={<Who name={change.name} lines={lines} />}
            {...(row.from !== undefined && { from: row.from })}
            to={row.to}
          />
        );
      })}
    </>
  );
}

export interface ImportOrgDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Downloads the current org structure (the template). */
  onDownloadCurrent: () => void;
  /** An export is in flight. */
  downloading?: boolean;
}

/** The drawer, reading and applying the file through the API. Remount it to start over. */
export function ImportOrgDrawer({
  open,
  onClose,
  onDownloadCurrent,
  downloading = false,
}: ImportOrgDrawerProps) {
  const { toast } = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [file, setFile] = useState<File | null>(null);
  const preview = usePreviewOrgImport();
  const apply = useApplyOrgImport();
  const busy = preview.isPending || apply.isPending;
  const changes = preview.data?.summary.length ?? 0;

  const read = () => {
    if (!file) return;
    setStep(2);
    preview.mutate(file);
  };

  const applyChanges = () => {
    if (!preview.data) return;
    apply.mutate(preview.data.id, {
      onSuccess: (applied) => {
        toast({
          tone: 'success',
          title: 'Org structure updated',
          description: applied.summary.map((line) => line.label).join(' · '),
        });
        onClose();
      },
      onError: (error) =>
        toast(
          withReference(
            {
              tone: 'danger',
              title: 'The changes couldn’t be applied',
              description:
                error instanceof ApiError && error.status < 500 && error.problem.detail
                  ? error.problem.detail
                  : 'Check your connection and try again.',
            },
            error,
          ),
        ),
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="right"
      size="md"
      title="Import org structure"
      description={<Stepper steps={IMPORT_STEPS} current={step} />}
      dismissable={!busy}
      footer={
        step === 1 ? (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={read} disabled={!file}>
              Continue
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                preview.reset();
                setStep(1);
              }}
              disabled={busy}
            >
              Back
            </Button>
            <Button
              onClick={applyChanges}
              disabled={!preview.data || changes === 0}
              loading={apply.isPending}
              loadingText="Applying…"
            >
              Apply changes
            </Button>
          </>
        )
      }
    >
      {step === 1 ? (
        <Stack gap={4}>
          <Dropzone
            accept={ACCEPT}
            title="Drop a CSV or Excel file here"
            description="or browse · .csv, .xlsx or .xlsm"
            onFiles={([first]) => setFile(first ?? null)}
          />
          {file && (
            <FileList
              files={[{ name: file.name, size: file.size }]}
              onRemove={() => setFile(null)}
            />
          )}
          <Alert tone="info" title="How the file is read">
            <List>
              <li>
                Recognized columns: <Col>USER ID</Col> (optional; it matches the user, so a new{' '}
                <Col>PAYEE EMAIL</Col> changes their email), <Col>PAYEE EMAIL</Col>,{' '}
                <Col>PAYEE NAME</Col> (new users only), <Col>ENABLED</Col> (Yes or No),{' '}
                <Col>ROLES</Col> (comma-separated, as in Users: Supervisor, Rep, Rep (intern), Pay
                viewer), <Col>SUPERVISOR</Col> (repeatable: <Col>SUPERVISOR 2</Col> and so on, by
                email), <Col>EXTERNAL ID: type</Col>.
              </li>
              <li>An email not already in the system creates a new user.</li>
              <li>
                Each user in the file has their supervisors <strong>replaced</strong>, not merged.
              </li>
              <li>
                A user row with no <Col>SUPERVISOR</Col> values ends up with no supervisors.
              </li>
              <li>
                A <Col>ROLES</Col> cell <strong>replaces</strong> the user’s roles. An empty{' '}
                <Col>ENABLED</Col>, <Col>ROLES</Col> or external ID cell leaves that as it is.
              </li>
              <li>
                Users not in the file are unchanged. Admins’ status and roles are changed in Edit
                user, not in the file.
              </li>
            </List>
          </Alert>
          <Stack gap={2} align="start">
            <Button
              size="sm"
              variant="secondary"
              leadingIcon="download"
              loading={downloading}
              loadingText="Exporting…"
              onClick={onDownloadCurrent}
            >
              Export current structure
            </Button>
            <Text tone="muted" size="meta">
              It’s in the import format: edit it and import it back.
            </Text>
          </Stack>
        </Stack>
      ) : (
        <ReviewStep
          preview={preview.data}
          error={preview.error}
          onRetry={() => file && preview.mutate(file)}
        />
      )}
    </Drawer>
  );
}

/** Step 2: the changes, while they're read, or why the file can't be imported. */
function ReviewStep({
  preview,
  error,
  onRetry,
}: {
  preview: OrgImportPreview | undefined;
  error: Error | null;
  onRetry: () => void;
}) {
  if (error) {
    const report = errorReportOf(error);
    if (report) return <ProblemReport report={report} />;
    const fileError = error instanceof ApiError && error.status < 500;
    return (
      <Alert
        tone="danger"
        title="The file couldn’t be read"
        action={
          fileError ? undefined : (
            <Button size="sm" variant="secondary" onClick={onRetry}>
              Retry
            </Button>
          )
        }
      >
        {fileError && error.problem.detail
          ? error.problem.detail
          : 'Check your connection and try again.'}
      </Alert>
    );
  }
  if (!preview) {
    return (
      <Stack gap={5} aria-busy="true">
        <VisuallyHidden>Reading the file…</VisuallyHidden>
        <SkeletonText lines={3} />
        <SkeletonText lines={6} />
      </Stack>
    );
  }
  return (
    <Stack gap={5}>
      <ChangeSummary
        items={preview.summary.map((line) => ({
          label: line.label,
          ...(line.tone && { tone: line.tone }),
        }))}
        emptyLabel="No changes: the file matches the current org structure."
      />
      {preview.affected.length > 0 && (
        <DiffList title="Affected users">
          {preview.affected.map((change) => (
            <AffectedRows key={change.userId ?? change.email} change={change} />
          ))}
        </DiffList>
      )}
    </Stack>
  );
}

/** The file's problems: nothing changes until they're fixed. */
function ProblemReport({ report }: { report: ErrorReport }) {
  return (
    <Stack gap={5}>
      <Alert tone="danger" title={report.summaryLine}>
        Nothing was changed. Correct the problems below and import the whole file again.
      </Alert>
      {/* Scroll, not cards: the drawer is narrow even on a desktop. */}
      <Table aria-label="Problems" layout="fixed">
        <TableHead>
          <TableCell width="10%" numeric>
            Row
          </TableCell>
          <TableCell width="22%">Payee</TableCell>
          <TableCell width="18%">Field</TableCell>
          <TableCell width="18%">Value</TableCell>
          <TableCell width="32%">Problem</TableCell>
        </TableHead>
        <TableBody>
          {report.problemRows.map((p, index) => (
            <TableRow key={`${p.row}-${p.field}-${index}`}>
              <TableCell label="Row" numeric tone="muted">
                {p.row}
              </TableCell>
              <TableCell rowHeader tone="strong">
                {p.payee}
              </TableCell>
              <TableCell label="Field" tone="muted">
                {p.field}
              </TableCell>
              <TableCell label="Value" tone="mono">
                {p.value}
              </TableCell>
              <TableCell label="Problem">{p.problem}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {report.omittedProblemRows > 0 && (
        <Text tone="muted" size="meta">
          {`And ${report.omittedProblemRows} more ${report.omittedProblemRows === 1 ? 'problem' : 'problems'}.`}
        </Text>
      )}
    </Stack>
  );
}
